import Box from '@mui/material/Box';
import { alpha, type Theme } from '@mui/material/styles';
import { memo, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { type AuthPageVariant } from '@/domain/auth/types';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { controlTokens, gray, surfaceTokens, radiusTokens } from '@/theme/themePrimitives';

interface Props {
  active: AuthPageVariant;
  children: ReactNode;
}

const AuthAccessCard = ({ active, children }: Props) => {
  const t = useTranslate('domain.auth.components.AuthAccessCard');
  // Switching between sign-in and sign-up keeps the invite token and e-mail of the query string.
  const { search } = useLocation();

  return (
    <Box sx={sx.card}>
      <Box component="nav" aria-label={t('accessLabel')} sx={sx.segment}>
        <Box
          component={RouterLink}
          to={{ pathname: ScreenRoutes.SIGNIN, search }}
          aria-current={active === 'signin' ? 'page' : undefined}
          sx={sx.segmentItem}
        >
          {t('signIn')}
        </Box>
        <Box
          component={RouterLink}
          to={{ pathname: ScreenRoutes.SIGNUP, search }}
          aria-current={active === 'signup' ? 'page' : undefined}
          sx={sx.segmentItem}
        >
          {t('signUp')}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

export default memo(AuthAccessCard);

const sx = {
  // Product paper uses equal corners; the crest belongs to brand compositions.
  card: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
    width: { xs: '100%', md: 440, lg: 472 },
    p: { xs: '24px 20px', sm: 4 },
    bgcolor: 'background.paper',
    border: `1px solid ${surfaceTokens.borderPaper}`,
    borderRadius: '24px',
    boxShadow: surfaceTokens.shadowRaised,
    '& .MuiDivider-root': {
      typography: 'overline',
      color: gray[500],
      fontSize: 11,
      letterSpacing: '0.1em',
      lineHeight: 1,
    },
    ...theme.applyStyles('dark', { borderColor: 'divider', boxShadow: 'none' }),
  }),
  segment: (theme: Theme) => ({
    display: 'flex',
    gap: '4px',
    p: '4px',
    borderRadius: radiusTokens.pill,
    bgcolor: surfaceTokens.borderSubtle,
    ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.common.white, 0.08) }),
  }),
  segmentItem: (theme: Theme) => ({
    typography: 'subtitle1',
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    px: 2,
    borderRadius: radiusTokens.pill,
    color: gray[600],
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
    transition: 'background-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
    '&:hover': { color: controlTokens.primary },
    '&:focus-visible': { outline: 0, boxShadow: controlTokens.focusRing },
    '&[aria-current="page"]': {
      bgcolor: surfaceTokens.paper,
      color: controlTokens.primary,
      fontWeight: 600,
      boxShadow: `0 1px 2px ${alpha(controlTokens.primary, 0.06)}, 0 6px 14px -4px ${alpha(controlTokens.primary, 0.12)}`,
    },
    ...theme.applyStyles('dark', {
      color: 'text.secondary',
      '&:hover': { color: 'text.primary' },
      '&[aria-current="page"]': {
        bgcolor: alpha(theme.palette.common.white, 0.14),
        color: 'text.primary',
        boxShadow: 'none',
      },
    }),
  }),
};
